import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';
import { getPendingFinancials } from '../services/gestor/gestor.service';

/**
 * Represents an authentication context object.
 * @typedef {Object} AuthContextTypes
 * @property {string} user - The user's name.
 * @property {number} id_empresa - The ID of the company.
 * @property {function} signIn - The sign-in function.
 * @property {function} signOut - The sign-out function.
 * @property {boolean} isAuth - Indicates if the user is authenticated.
 */

const AuthContext = createContext({});
const TOKEN_KEY = '@delivery-Token';
const USER = '@delivery-User';
const TOKEN_API = '@socket-token';
const BLOQUEIO_KEY = '@delivery-Bloqueio';

const AuthProvider = ({ children }) => {
  const [data, setData] = useState(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = localStorage.getItem(USER);

    if (token && user) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const usr = JSON.parse(atob(user));
      const bloqueioRaw = localStorage.getItem(BLOQUEIO_KEY);
      const bloqueioData = bloqueioRaw ? JSON.parse(bloqueioRaw) : null;
      return {
        token,
        user: usr,
        id_empresa: usr.IdEmpresa,
        bloqueio: bloqueioData?.bloqueio ?? null,
        boletos: bloqueioData?.boletos ?? [],
      };
    }

    return {};
  });
  const isAuth = !!data?.user?.Id;

  const syncBloqueioFinanceiro = useCallback(async (id_empresa, bloqueado) => {
    try {
      await api.patch(`configuracao/bloqueio-financeiro/${id_empresa}`, {
        bloqueado,
      });
    } catch (error) {
      console.log(error);
    }
  }, []);

  const signIn = useCallback(async (company_id, login, password) => {
    const response = await api.post('/auth/gestor', {
      IdEmpresa: company_id,
      Login: login,
      Senha: password,
    });
    const access_token = response?.data?.access_token;
    const user = response?.data?.user;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER, btoa(JSON.stringify(user)));
    localStorage.setItem(TOKEN_API, JSON.stringify(user));

    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    const financeiro = await getPendingFinancials(user.IdEmpresa);
    const bloqueio = financeiro?.Bloqueio ?? null;
    const boletos = financeiro?.Boletos ?? [];

    if (bloqueio) {
      localStorage.setItem(BLOQUEIO_KEY, JSON.stringify({ bloqueio, boletos }));
    } else {
      localStorage.removeItem(BLOQUEIO_KEY);
    }

    await syncBloqueioFinanceiro(user.IdEmpresa, !!bloqueio);

    setData({
      token: access_token,
      user,
      id_empresa: user.IdEmpresa,
      bloqueio,
      boletos,
    });
  }, [syncBloqueioFinanceiro]);

  const refreshBloqueio = useCallback(async () => {
    if (!data?.user?.IdEmpresa) {
      return null;
    }

    const financeiro = await getPendingFinancials(data.user.IdEmpresa);
    const bloqueio = financeiro?.Bloqueio ?? null;
    const boletos = financeiro?.Boletos ?? [];

    if (bloqueio) {
      localStorage.setItem(BLOQUEIO_KEY, JSON.stringify({ bloqueio, boletos }));
    } else {
      localStorage.removeItem(BLOQUEIO_KEY);
    }

    await syncBloqueioFinanceiro(data.user.IdEmpresa, !!bloqueio);

    setData((prev) => ({ ...prev, bloqueio, boletos }));

    return bloqueio;
  }, [data?.user?.IdEmpresa, syncBloqueioFinanceiro]);

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER);
    localStorage.removeItem(TOKEN_API);
    localStorage.removeItem(BLOQUEIO_KEY);
    localStorage.clear();
    setData({});
  }, []);

  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER);
          localStorage.removeItem(TOKEN_API);

          window.location.pathname = '/';
          setData({});
        }
        return Promise.reject(error);
      },
    );

    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        id_empresa: data.id_empresa,
        permissoes: data.user?.permissoes ?? null,
        bloqueio: data.bloqueio ?? null,
        boletos: data.boletos ?? [],
        signIn,
        signOut,
        refreshBloqueio,
        isAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  /**
   * Example authentication object.
   * @type {AuthContextTypes}
   */
  const {
    user,
    id_empresa,
    permissoes,
    bloqueio,
    boletos,
    signIn,
    signOut,
    refreshBloqueio,
    isAuth,
  } = useContext(AuthContext);

  return {
    user,
    id_empresa,
    permissoes,
    bloqueio,
    boletos,
    signIn,
    signOut,
    refreshBloqueio,
    isAuth,
  };
}

export { AuthProvider, useAuth };
